import React, { useEffect, useState } from 'react';
import config from '../../../../shared/services/config';
import { EMPTY_ARRAY, EMPTY_OBJECT, FALSE, NOOP } from '../../../../shared/utils/common';
import { TableHeader, TableBody, DetailsCard, TableLoader, Error } from '../../../../shared/components/table';
import { usePagination } from '../../../../shared/hooks/usePagination';
import { useBreakpoint } from '../../../../shared/hooks/useBreakpoint';
import { useDataFilter } from '../../../../shared/hooks/useDataFilter';
import { PageHeader, PageHeaderLoader } from './page-header';

const HEADERS = [
  { label: 'Business Name', mobile: 1, id: 'businessName' },
  { label: 'MID', isSortable: true, mobile: 2, id: 'mid' },
  { label: 'Role', mobile: 3, id: 'role' },
]

export const TeamAccountsTable = ({
  teamAccounts = EMPTY_ARRAY,
  onSelection = NOOP,
  isLoading: isLoadingProp = FALSE
}) => {
  const isMobile = useBreakpoint('m')
  const records = teamAccounts.map(({ business_name, identifier, acl_role_name, totalMids }) => ([
    <>
      {business_name}
      <br />
      {(totalMids > 1 ? `+${totalMids - 1} more` : '')}
    </>
    ,
    identifier,
    acl_role_name == 'MerchantAdminUserRole' ? 'Owner' : acl_role_name
  ]))
  const [searchTerm, setSearchTerm] = useState('');
  const [orderProps, setOrderProps] = useState(EMPTY_OBJECT);
  const [pageSize, setPageSize] = useState(10);
  const { filteredData, updated } = useDataFilter({
    records, searchTerm, searchCols: [0, 1], ...orderProps
  });
  const {
    page,
    isLoading,
    goToPage,
    currentPage
  } = usePagination({ records: filteredData, pageSize, forceRecordUpdate: updated })

  const orderToggles = {
    asc: 'desc',
    desc: 'asc'
  }
  const tableProps = {
    headers: HEADERS,
    data: filteredData,
    searchTerm,
    orderProps,
    page,
    isLoading,
    goToPage,
    currentPage,
    pageSize,
    setPageSize,
    resetFilter: () => setSearchTerm('')
  }

  const handleOnSelection = (mid) => {
    onSelection(teamAccounts.find(v => v.identifier == mid))
  }

  return <div className='tw-preflight'>
    {isLoadingProp ? (
      <div className='tw-flex tw-flex-col tw-gap-24 tw-py-16'>
        <PageHeaderLoader />
        <TableLoader />
      </div>
    ) : (
      <>
        <PageHeader
          title='Welcome back!'
          subTitle='Select the MID dashboard you would like to view'
          hideSearch={teamAccounts.length <= 6}
          searchPlaceholder='Search MID or Name'
          searchTerm={searchTerm}
          onSearchTermChange={e => setSearchTerm(e.target.value)}
        />
        <div className='tw-py-24'>
          {
            teamAccounts.length <= 6 ? <div className='tw-py-32'><CardsGrid data={records} onSelection={handleOnSelection} /></div> :
              (isMobile ? <MobileTable {...tableProps} pageSize={10} onRowSelection={handleOnSelection} /> : <WebTable
                {...tableProps}
                onRowSelection={handleOnSelection}
                onOrderChange={(orderCol) =>
                  setOrderProps({ orderCol, order: orderToggles[orderProps?.order] || orderToggles.desc })}
              />)
          }
        </div>
      </>
    )}
  </div>
}

export const CardsGrid = ({ data = EMPTY_ARRAY, onSelection = NOOP }) => {
  const isMobile = useBreakpoint('m')

  const renderCards = (ids = EMPTY_ARRAY) => data.map(([name, mid, role], id) => ids.indexOf(id) >= 0 ? (
    <DetailsCard
      className={isMobile ? 'tw-w-full' : 'tw-w-200'}
      mid={mid}
      businessName={name}
      aclRoleName={role}
      onClick={() => onSelection(mid)}
    />) : null)
  return (data.length <= 3 || data.length > 4) && data.length != 6 ? <div className='tw-flex tw-flex-wrap tw-gap-24 tw-justify-center'>
    {renderCards([0, 1, 2, 3, 4, 5])}
  </div> : <div className='tw-flex tw-flex-col tw-gap-24'>
    <div className='tw-flex tw-flex-wrap tw-gap-24 tw-justify-center'>
      {renderCards(data.length == 4 ? [0, 1] : [0, 1, 2])}
    </div>
    <div className='tw-flex tw-flex-wrap tw-gap-24 tw-justify-center'>
      {renderCards(data.length == 4 ? [2, 3] : [3, 4, 5])}
    </div>
  </div>
}

export const MobileTable = ({ data = EMPTY_ARRAY, headers = EMPTY_ARRAY, onRowSelection = NOOP, pageSize }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadMoreData, setLoadMoreData] = useState(0)
  const UI = config.get('uiv2', {});
  const [pagesToShow, setPagesToShow] = useState(1);
  const bodyData = data.map(([businessName, mid, role]) => ({ mid, businessName, role }))

  useEffect(() => {
    if (loadMoreData > 0) {
      setIsLoading(true)
      const id = setTimeout(() => {
        if (bodyData?.length > pageSize * pagesToShow)
          setPagesToShow(c => c + 1)
        setIsLoading(false)
      }, 100)
      return () => clearTimeout(id)
    }
  }, [loadMoreData])
  return <UI.BaseTable
    bodyData={bodyData.filter((v, i) => i < pagesToShow * pageSize)}
    headData={headers}
    clickableRow={{
      enable: true,
      onRowClick: ({ mid }) => onRowSelection(mid)
    }}
    pagination={{
      enable: true,
      totalRows: bodyData.length,
      onBottomHit: () => setLoadMoreData(c => c + 1)
    }}
    isLoading={isLoading}
  />
}

export const WebTable = ({
  data = EMPTY_ARRAY,
  headers = EMPTY_ARRAY,
  onRowSelection = NOOP,
  onOrderChange = NOOP,
  orderProps = EMPTY_OBJECT,
  page,
  isLoading,
  goToPage,
  currentPage,
  setPageSize,
  pageSize,
  resetFilter = NOOP
}) => {
  const UI = config.get('uiv2', {});

  const colRatios = [1, 1, 2]
  return (
    <>
      <TableHeader headers={headers} onOrderChange={onOrderChange} {...orderProps} colRatios={colRatios} />
      {
        isLoading ? <TableLoader /> : (data.length == 0 ? (
          <Error
            actions={[
              {
                onClick: resetFilter,
                size: 'medium',
                text: 'Reset Filters',
                variant: 'PRIMARY'
              }
            ]}
            heading='Suggest removing filters'
            description='You may try to remove filters or reset to view the data'
            message='NO RESULT FOUND'
            infographicType='filter-search'
          />
        ) : <>
          <TableBody data={page} colRatios={colRatios} onRowClick={([, mid,]) => onRowSelection(mid)} />
          <UI.Pagination
            className='co-paginations-container'
            totalRows={data.length}
            currentPage={currentPage + 1}
            rowsPerPage={pageSize}
            onPageChange={(pageNo) => goToPage({ pageNo: pageNo - 1 })}
            onRowsPerPageChange={setPageSize}
            isLoading={isLoading}
            rowPerPageOptions={[
              {
                label: '10',
                value: 10,
                checked: pageSize == 10
              },
              {
                label: '50',
                value: 50,
                checked: pageSize == 50
              }
            ]}
          />
        </>)
      }

    </>
  )
}